import { DestinationType } from './Integration.interface';

export const UNKNOWN_DELIVERY_GROUP = '__unknown__';

export type DestinationRateLimitPeriod = 'second' | 'minute' | 'hour' | 'concurrent';
export type DeliveryGroupRateLimitPeriod = 'second' | 'minute' | 'hour';

export interface DeliveryGroupOverride {
  rate_limit: number;
  rate_limit_period: DeliveryGroupRateLimitPeriod;
}

export interface DeliveryGroupsConfig {
  key: string;
  rate_limit: number;
  rate_limit_period: DeliveryGroupRateLimitPeriod;
  overrides?: { [delivery_group: string]: DeliveryGroupOverride } | null;
}

export interface APIDeliveryPolicyGroupOverride {
  rate: number;
  rate_period: DeliveryGroupRateLimitPeriod;
}

export interface APIDeliveryPolicyGroups {
  key: string;
  rate: number;
  rate_period: DeliveryGroupRateLimitPeriod;
  overrides?: { [delivery_group: string]: APIDeliveryPolicyGroupOverride } | null;
}

export interface APIDeliveryPolicy {
  rate?: number | null;
  period?: DestinationRateLimitPeriod | null;
  groups?: APIDeliveryPolicyGroups | null;
}

// Also the set an attempt may be delivered with — an attempt inherits the inbound
// method when the Destination sets no override, so anything a Source can accept has to
// be valid here too. See `EventAttemptSchema.http_method`.
export const destination_http_methods = [
  'GET',
  'HEAD',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
  'QUERY',
] as const;
export type DestinationHTTPMethod = (typeof destination_http_methods)[number];

export const destination_auth_method_types = [
  'HOOKDECK_SIGNATURE',
  'BASIC_AUTH',
  'API_KEY',
  'BEARER_TOKEN',
  'CUSTOM_SIGNATURE',
  'OAUTH2_CLIENT_CREDENTIALS',
  'OAUTH2_AUTHORIZATION_CODE',
  'AWS_SIGNATURE',
  'GCP_SERVICE_ACCOUNT',
  'STANDARD_WEBHOOKS',
] as const;
export type DestinationAuthMethodType = (typeof destination_auth_method_types)[number];

export interface DestinationAuthMethodHookdeckSignatureConfig {}

export interface DestinationAuthMethodBasicAuthConfig {
  username: string;
  password: string;
}

export interface DestinationAuthMethodApiKeyConfig {
  key: string;
  api_key: string;
  to: 'header' | 'query';
}

export interface DestinationAuthMethodBearerTokenConfig {
  token: string;
}

export interface DestinationAuthMethodCustomSignatureConfig {
  key: string;
  signing_secret: string;
}

export interface DestinationAuthMethodOAuth2ClientCredentialsConfig {
  client_id: string;
  client_secret: string;
  scope?: string;
  auth_server: string;
  authentication_type: string;
}

export interface DestinationAuthMethodOAuth2AuthorizationCodeConfig {
  client_id: string;
  client_secret: string;
  refresh_token: string;
  scope?: string;
  auth_server: string;
  authentication_type: string;
}

export interface DestinationAuthMethodAwsSignatureConfig {
  access_key_id: string;
  secret_access_key: string;
  region?: string | null;
  service?: string | null;
}

export interface DestinationAuthMethodGcpServiceAccountConfig {
  service_account_key: string;
  scope?: string | null;
}

export interface DestinationAuthMethodStandardWebhooksConfig {
  webhook_secret_key: string;
}

export type DestinationAuthMethodConfig =
  | DestinationAuthMethodHookdeckSignatureConfig
  | DestinationAuthMethodBasicAuthConfig
  | DestinationAuthMethodApiKeyConfig
  | DestinationAuthMethodBearerTokenConfig
  | DestinationAuthMethodCustomSignatureConfig
  | DestinationAuthMethodOAuth2ClientCredentialsConfig
  | DestinationAuthMethodOAuth2AuthorizationCodeConfig
  | DestinationAuthMethodAwsSignatureConfig
  | DestinationAuthMethodGcpServiceAccountConfig
  | DestinationAuthMethodStandardWebhooksConfig;

export interface DestinationAuthMethod {
  type: DestinationAuthMethodType;
  config: DestinationAuthMethodConfig;
}

export interface Destination {
  id: string;
  team_id: string;
  name: string;
  description: string | null;
  type: DestinationType;
  path_forwarding_disabled: boolean;
  url?: string;
  cli_path?: string;
  rate_limit?: number;
  rate_limit_period?: DestinationRateLimitPeriod;
  delivery_groups?: DeliveryGroupsConfig | null;
  // Canonical per-destination setting. Null temporarily inherits the legacy subscription
  // `attempt_timeout` during rollout; the subscription fallback will be removed after backfill.
  delivery_timeout?: number | null;
  http_method?: DestinationHTTPMethod | null;
  auth_method: DestinationAuthMethod;

  rl_migration_status?: string | null;

  deleted_at?: string;
  disabled_at?: string | null;
  updated_at: string;
  created_at: string;
}

export interface HTTPDestinationTypeConfig {
  url: string;
  path_forwarding_disabled: boolean;
  delivery_policy: APIDeliveryPolicy | null;
  delivery_timeout?: number | null;
  http_method: DestinationHTTPMethod | null;
  auth_type: DestinationAuthMethodType | null;
  auth: DestinationAuthMethodConfig | null;
}

export interface CLIDestinationTypeConfig {
  path: string;
  path_forwarding_disabled: boolean;
  delivery_timeout?: number | null;
  http_method: DestinationHTTPMethod | null;
  auth_type: DestinationAuthMethodType | null;
  auth: DestinationAuthMethodConfig | null;
}

export interface MockDestinationTypeConfig {
  path_forwarding_disabled: boolean;
  delivery_policy: APIDeliveryPolicy | null;
  delivery_timeout?: number | null;
  http_method: DestinationHTTPMethod | null;
  auth_type: DestinationAuthMethodType | null;
  auth: DestinationAuthMethodConfig | null;
}

export type DestinationConfig =
  | HTTPDestinationTypeConfig
  | CLIDestinationTypeConfig
  | MockDestinationTypeConfig;

export interface APIDestination
  extends Omit<
    Destination,
    | 'auth_method'
    | 'url'
    | 'cli_path'
    | 'rate_limit'
    | 'rate_limit_period'
    | 'delivery_groups'
    | 'delivery_timeout'
    | 'http_method'
    | 'path_forwarding_disabled'
  > {
  type: DestinationType;
  config: DestinationConfig;
}

export interface DestinationCreateInput {
  id?: string;
  team_id: string;
  name: string;
  description?: string | null;
  type?: DestinationType;
  url?: string;
  cli_path?: string;
  rate_limit?: number;
  rate_limit_period?: DestinationRateLimitPeriod;
  delivery_groups?: DeliveryGroupsConfig | null;
  delivery_timeout?: number | null;
  path_forwarding_disabled?: boolean;
  http_method?: DestinationHTTPMethod;
  auth_method?: DestinationAuthMethod;
  config?: {
    type?: DestinationAuthMethodType;
    auth?: DestinationAuthMethodConfig;
  };
  iv?: string;

  // Temporary for rl state migration
  rl_migration_status?: string | null;
}

export interface DestinationUpdateInput
  extends Partial<
    Pick<
      Destination,
      | 'name'
      | 'type'
      | 'description'
      | 'path_forwarding_disabled'
      | 'url'
      | 'cli_path'
      | 'rate_limit'
      | 'rate_limit_period'
      | 'delivery_groups'
      | 'delivery_timeout'
      | 'http_method'
      | 'auth_method'
      | 'deleted_at'
    >
  > {
  disabled_at?: string;
  iv?: string;

  // Temporary for rl state migration
  rl_migration_status?: string | null;
}
